import { delayedToggleMultiline } from './textdynamic.js';

export function initForms() {
  const mainForm =
    document.querySelector('#wr-main-contact-form') ||
    document.querySelector('#main-contact-form');

  if (mainForm) {
    setupMainForm(mainForm);
  }

  const aiCheckForm = document.querySelector('#wr-ai-check-form');
  if (mainForm) {
    attachSubmitHandler(mainForm);
  }
  if (aiCheckForm) {
    attachAiCheckSubmitHandler(aiCheckForm);
  }

  const questionForm = document.querySelector('.question-form');
  if (questionForm) {
    setupQuestionForm(questionForm);
  }
}

function attachAiCheckSubmitHandler(form) {
  const submitButton = form.querySelector('[type="submit"]');
  const domainInput = form.querySelector('#ai-check-website');
  const emailInput = form.querySelector('#ai-email');
  const sendEmailCheckbox = form.querySelector('#ai-send-result-email');
  const statusBox = form.querySelector('#wr-ai-check-status');

  const getLanguage = () =>
    document.body.classList.contains('set-lang-en') ? 'en' : 'de';

  const getStatusText = (key) => {
    const lang = getLanguage();
    const de = {
      starting: 'Analyse wird gestartet. Weiterleitung zur Live-Ansicht ...',
      redirecting: 'Weiterleitung zur Live-Ansicht ...',
      invalidInput: 'Bitte pr\u00fcfen Sie die Eingaben und versuchen Sie es erneut.',
      rateLimitIp: 'Zu viele Starts von dieser IP. Bitte sp\u00e4ter erneut versuchen.',
      rateLimitDomain: 'Zu viele Starts f\u00fcr diese Domain. Bitte sp\u00e4ter erneut versuchen.',
      serverError: 'Analyse konnte gerade nicht gestartet werden. Bitte erneut versuchen.',
    };
    const en = {
      starting: 'Starting analysis. Redirecting to live view ...',
      redirecting: 'Redirecting to live view ...',
      invalidInput: 'Please check your input and try again.',
      rateLimitIp: 'Too many starts from this IP. Please try again later.',
      rateLimitDomain: 'Too many starts for this domain. Please try again later.',
      serverError: 'Analysis could not be started right now. Please try again.',
    };
    const source = lang === 'en' ? en : de;
    return source[key] || source.serverError;
  };

  const setStatus = (kind, key) => {
    if (!statusBox) return;
    statusBox.hidden = false;
    statusBox.classList.remove('is-progress', 'is-error', 'is-success');
    statusBox.classList.add(kind);
    statusBox.textContent = getStatusText(key);
  };

  const clearStatus = () => {
    if (!statusBox) return;
    statusBox.hidden = true;
    statusBox.classList.remove('is-progress', 'is-error', 'is-success');
    statusBox.textContent = '';
  };

  const setSubmitting = (isSubmitting) => {
    if (!submitButton) return;
    submitButton.disabled = isSubmitting;
    submitButton.classList.toggle('is-submitting', isSubmitting);
  };

  const syncEmailField = () => {
    if (!emailInput || !sendEmailCheckbox) return;
    const enabled = Boolean(sendEmailCheckbox.checked);
    emailInput.disabled = !enabled;
    emailInput.required = enabled;
    if (!enabled) {
      emailInput.value = '';
      emailInput.setCustomValidity('');
    }
  };

  sendEmailCheckbox?.addEventListener('change', () => {
    syncEmailField();
    clearStatus();
  });
  domainInput?.addEventListener('input', clearStatus);
  emailInput?.addEventListener('input', clearStatus);
  syncEmailField();

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    clearStatus();

    if (!domainInput || !domainInput.checkValidity()) {
      domainInput?.reportValidity();
      setStatus('is-error', 'invalidInput');
      return;
    }
    if (!emailInput?.disabled && !emailInput.checkValidity()) {
      emailInput.reportValidity();
      setStatus('is-error', 'invalidInput');
      return;
    }

    const payload = {
      input_url: String(domainInput.value || '').trim(),
      email: emailInput && !emailInput.disabled ? String(emailInput.value || '').trim() : null,
      send_result_email: Boolean(sendEmailCheckbox?.checked),
      language: getLanguage(),
    };

    setSubmitting(true);
    setStatus('is-progress', 'starting');

    try {
      const response = await fetch('/api/public/ai-checks/start', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        const detail = String((data && data.detail) || '').toLowerCase();
        if (response.status === 429 && detail.includes('domain')) {
          setStatus('is-error', 'rateLimitDomain');
          return;
        }
        if (response.status === 429) {
          setStatus('is-error', 'rateLimitIp');
          return;
        }
        if (response.status >= 400 && response.status < 500) {
          setStatus('is-error', 'invalidInput');
          return;
        }
        setStatus('is-error', 'serverError');
        return;
      }

      const liveUrl = String((data && data.live_url) || '').trim();
      if (!liveUrl) {
        setStatus('is-error', 'serverError');
        return;
      }

      setStatus('is-success', 'redirecting');
      window.location.assign(liveUrl);
    } catch (err) {
      setStatus('is-error', 'serverError');
    } finally {
      setSubmitting(false);
    }
  });
}

function setupMainForm(mainForm) {
  mainForm.setAttribute('novalidate', '');

  const aiCheckbox = mainForm.querySelector('#interest-ai-check');
  const aiUrlCard = mainForm.querySelector('.ai-url-card');
  const aiWebsiteInput = mainForm.querySelector('#ai-website');

  const toggleAiUrlField = () => {
    const checked = aiCheckbox?.checked;
    if (aiUrlCard) {
      aiUrlCard.classList.toggle('wr-form-hidden', !checked);
    }
    if (aiWebsiteInput) {
      aiWebsiteInput.disabled = !checked;
      if (!checked) {
        aiWebsiteInput.value = '';
        aiWebsiteInput.setCustomValidity('');
      }
    }
  };

  aiCheckbox?.addEventListener('change', toggleAiUrlField);
  toggleAiUrlField();

  const languageFromBody = () =>
    document.body.classList.contains('set-lang-en') ? 'en' : 'de';
  const formFieldErrors = new WeakMap();

  const createErrorElement = (input) => {
    const wrapper = document.createElement('div');
    wrapper.className = 'wr-input-error';
    const icon = document.createElement('span');
    icon.className = 'wr-input-error-icon';
    const text = document.createElement('span');
    text.className = 'wr-input-error-text';
    wrapper.append(icon, text);
    input.insertAdjacentElement('afterend', wrapper);
    return wrapper;
  };

  const getErrorMessage = (input) => {
    const lang = languageFromBody();
    return (
      input.getAttribute(`data-error-${lang}`) ||
      input.validationMessage ||
      input.getAttribute('data-error-de') ||
      ''
    );
  };

  const displayInputError = (input, message) => {
    if (!message) return;
    let el = formFieldErrors.get(input);
    if (!el) {
      el = createErrorElement(input);
      formFieldErrors.set(input, el);
    }
    el.querySelector('.wr-input-error-text').textContent = message;
    el.classList.add('is-active');
  };

  const clearInputError = (input) => {
    const el = formFieldErrors.get(input);
    if (el) {
      el.classList.remove('is-active');
    }
  };

  mainForm.querySelectorAll('input, textarea').forEach((field) => {
    field.addEventListener('invalid', (event) => {
      event.preventDefault();
      displayInputError(field, getErrorMessage(field));
    });
    field.addEventListener('input', () => {
      clearInputError(field);
    });
  });

  const steps = mainForm.querySelectorAll('.form-step');
  const nextButtons = mainForm.querySelectorAll('.form-btn-next');
  const prevButtons = mainForm.querySelectorAll('.form-btn-prev');
  let currentStep = 0;

  function showStep(index) {
    steps.forEach((step, i) => step.classList.toggle('active', i === index));
    delayedToggleMultiline();
    updateDotState();
  }

  function groupInputsByName(inputs) {
    const groups = {};
    inputs.forEach((input) => {
      if (!groups[input.name]) groups[input.name] = [];
      groups[input.name].push(input);
    });
    return Object.values(groups);
  }

  function updateDotState() {
    if (!steps || steps.length === 0) return;
    const stepEl = steps[currentStep];
    if (!stepEl) return;
    const allInputs = Array.from(stepEl.querySelectorAll('input, textarea'));

    const others = allInputs.filter(
      (i) => i.type !== 'radio' && i.type !== 'checkbox'
    );
    const anyOtherValid = others.some((i) => i.checkValidity());

    const radios = allInputs.filter((i) => i.type === 'radio');
    const radioGroups = groupInputsByName(radios);
    const anyRadioValid = radioGroups.some((group) =>
      group.some((i) => i.checked)
    );

    const checkboxes = allInputs.filter((i) => i.type === 'checkbox');
    const checkboxGroups = groupInputsByName(checkboxes);
    const anyCheckboxValid = checkboxGroups.some((group) =>
      group.some((i) => i.checked)
    );

    const anyValid = anyOtherValid || anyRadioValid || anyCheckboxValid;
    stepEl.classList.toggle('form-dots-active', anyValid);
  }

  function attachInputListeners() {
    const all = mainForm.querySelectorAll('input, textarea');
    all.forEach((inp) => {
      inp.addEventListener('input', updateDotState);
      inp.addEventListener('change', updateDotState);
    });
  }

  function validateGroupedInputs(stepElement, lang) {
    let valid = true;
    const checkboxGroups = groupInputsByName(
      stepElement.querySelectorAll('input[type="checkbox"]')
    );
    const radioGroups = groupInputsByName(
      stepElement.querySelectorAll('input[type="radio"]')
    );
    [...checkboxGroups, ...radioGroups].forEach((group) => {
      const anyChecked = group.some((i) => i.checked);
      if (!anyChecked) {
        const err = group[0].getAttribute(`data-error-${lang}`) || '';
        group.forEach((i) => i.setCustomValidity(err));
        displayInputError(group[0], err);
        valid = false;
      } else {
        group.forEach((i) => i.setCustomValidity(''));
      }
    });
    return valid;
  }

  function validateIndividualInputs(inputs) {
    for (const input of inputs) {
      if (input.disabled) continue;
      if (!input.checkValidity()) {
        displayInputError(input, getErrorMessage(input));
        return false;
      }
    }
    return true;
  }

  nextButtons.forEach((btn) => {
    btn.addEventListener('click', () => {
      const stepEl = steps[currentStep];
      const lang = localStorage.getItem('language') || 'en';
      const inputs = stepEl.querySelectorAll('input, textarea');
      const okGroup = validateGroupedInputs(stepEl, lang);
      const okInd = validateIndividualInputs(inputs);
      if (okGroup && okInd && currentStep < steps.length - 1) {
        currentStep++;
        showStep(currentStep);
      }
    });
  });

  prevButtons.forEach((btn) => {
    btn.addEventListener('click', () => {
      if (currentStep > 0) {
        currentStep--;
        showStep(currentStep);
      }
    });
  });

  attachInputListeners();
  showStep(currentStep);
}

function attachSubmitHandler(form) {
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const notesField = form.querySelector('[name="ai_notes"]');
    const hiddenMessage = form.querySelector('input[name="message"]');
    if (notesField && hiddenMessage) {
      const notes = String(notesField.value || '').trim();
      if (notes) {
        hiddenMessage.value = `Live Website-Check Anfrage: ${notes}`;
      }
    }

    const action = form.getAttribute('action') || '/api/contact/submit';
    const submitButton = form.querySelector('[type="submit"]');
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.classList.add('is-submitting');
    }

    let ok = false;
    try {
      const response = await fetch(action, {
        method: 'POST',
        body: new FormData(form),
      });
      if (response.ok) {
        const payload = await response.json().catch(() => ({}));
        ok = payload && payload.ok === true;
      }
    } catch (err) {
      ok = false;
    }

    const statusParam = ok ? '1' : '0';
    window.location.assign(`/?sent=${statusParam}#section-contact`);
  });
}

function setupQuestionForm(questionForm) {
  const stepsQ = questionForm.querySelectorAll('.form-step');
  const nextBtn = questionForm.querySelector('.form-btn-next');
  const frageInput = questionForm.querySelector('#frage-mini');
  const telefonInput = questionForm.querySelector('#telefon-mini');
  let currentStepQ = 0;

  function showStepQ(index) {
    stepsQ.forEach((step, i) => step.classList.toggle('active', i === index));
    updateDotStateQ();
  }

  function updateDotStateQ() {
    const step1 = stepsQ[0];
    const frageValid = frageInput && frageInput.checkValidity();
    step1?.classList.toggle('form-dots-active', Boolean(frageValid));

    const step2 = stepsQ[1];
    const telefonValid = telefonInput && telefonInput.checkValidity();
    step2?.classList.toggle('form-dots-active', Boolean(telefonValid));
  }

  frageInput?.addEventListener('input', updateDotStateQ);
  frageInput?.addEventListener('change', updateDotStateQ);
  telefonInput?.addEventListener('input', updateDotStateQ);
  telefonInput?.addEventListener('change', updateDotStateQ);

  nextBtn?.addEventListener('click', (e) => {
    e.preventDefault();
    if (!frageInput?.checkValidity()) {
      frageInput?.reportValidity();
      return;
    }
    currentStepQ = 1;
    showStepQ(currentStepQ);
  });
}

export function handleSubmissionParams() {
  const urlParams = new URLSearchParams(window.location.search);

  if (urlParams.get('sent') === '1') {
    document.body.classList.add('main-contact-submitted-true');
    document.body.dataset.contactStatus = 'success';
    document.body.classList.add('contact-submit-overlay-open');
  }
  if (urlParams.get('sent') === '0') {
    document.body.classList.add('main-contact-submitted-false');
    document.body.dataset.contactStatus = 'error';
    document.body.classList.add('contact-submit-overlay-open');
  }

  if (urlParams.get('question_submitted') === 'true') {
    const questionContainer = document.querySelector('.more-questions-area');
    questionContainer?.classList.add('question-sent');
    if (questionContainer) {
      questionContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  const overlay = document.querySelector('#wr-contact-submit-overlay');
  const closeBtn = document.querySelector('.wr-contact-submit-close');
  const closeOverlay = () => {
    document.body.classList.remove('contact-submit-overlay-open');
    delete document.body.dataset.contactStatus;
    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.delete('sent');
    window.history.replaceState({}, '', cleanUrl.toString());
  };
  closeBtn?.addEventListener('click', closeOverlay);
  overlay?.addEventListener('click', (event) => {
    if (event.target === overlay) closeOverlay();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeOverlay();
  });
}
